import Phaser from 'phaser';

import * as ABCya from '@abcya/abcya_phaser';
import ImageKeys from '../UIAssetKeys';
import CubeButton, { CubeButtonEvents } from '../CubeButton';
import Shape from './Shape';
import FontKeys from '../FontKeys';
import Game, { GameEvents, GameStates } from '../scenes/Game';
import GameData, { REGIONS, SHAPES } from '../gameData';
import SoundAssetKeys from '../SoundAssetKeys';

export default class ShapeReview extends Phaser.GameObjects.Container
{
    /**
     * Create the Shape Review screens
     * @param {Game} scene the owner scene
     */
    constructor(scene)
    {
        super(scene);

        this.scene = scene;

        this.scene.add.existing(this);

        this._addHeader();
        this._addButtons();
        this._addShapes();
        this._addFooter();

        //this._addReference("ShapeReview1", "assets/reference_images/2 Get in Shape-Review1.jpg");
        //this._addReference("ShapeReview2", "assets/reference_images/3 Get in Shape-Review2.jpg");
        //this._addReference("ShapeReview3", "assets/reference_images/4 Get in Shape-Review3.jpg");

        this.reset();
    }

    /**
     * Reset should be called when you want to set the state to a disabled/inactive state.
     */
    reset()
    {
        // send everything off screen and set alpha to 0
        this._sendTargetsOffScreen();
        this.setAlpha(0);

        this._currentState = ShapeReviewStates.FLAT_SHAPES;

        this.menuButton.disableInteractive();
        this.nextButton.disableInteractive();
        this.backButton.disableInteractive();
    }

    /**
     * Prepare should be called before you want to prepare the state to become active.
     */
    prepare()
    {
        this.setAlpha(1);
        this._sendTargetsOffScreen();

        this.menuButton.disableInteractive();
        this.nextButton.disableInteractive();
        this.backButton.disableInteractive();
    }

    /**
     * Animates the states in.
     */
    async begin()
    {
        ABCya.Audio.playOneShot(SoundAssetKeys._shapes_review_tween, this.scene, 1, false, 0.25);

        let tweens = [
            { targets: this.letterS, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterS.onScreenPosition.y } },
            { targets: this.letterH, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterH.onScreenPosition.y }, offset: 50 },
            { targets: this.letterA, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterA.onScreenPosition.y }, offset: 100 },
            { targets: this.letterP, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterP.onScreenPosition.y }, offset: 150 },
            { targets: this.letterE1, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterE1.onScreenPosition.y }, offset: 200 },
            { targets: this.letterR, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterR.onScreenPosition.y }, offset: 250 },
            { targets: this.letterE2, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterE2.onScreenPosition.y }, offset: 300 },
            { targets: this.letterV, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterV.onScreenPosition.y }, offset: 350 },
            { targets: this.letterI, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterI.onScreenPosition.y }, offset: 400 },
            { targets: this.letterE3, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterE3.onScreenPosition.y }, offset: 450 },
            { targets: this.letterW, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterW.onScreenPosition.y }, offset: 500 },
            { targets: this.flatShapesTop, duration: 1000, ease: Phaser.Math.Easing.Quadratic.Out, props: { x: this.flatShapesTop.onScreenPosition.x }, offset: 0 },
            { targets: this.flatShapesBottom, duration: 1000, ease: Phaser.Math.Easing.Quadratic.Out, props: { x: this.flatShapesBottom.onScreenPosition.x }, offset: 0 },
            { targets: this.flatShapesFooter, duration: 1000, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: 0 },
            { targets: this.menuButton, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { scale: 1 }, offset: 300 },
            { targets: this.backButton, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { scale: 1 }, offset: 400 },
            { targets: this.nextButton, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { scale: 1 }, offset: 500 },
        ];

        let timeline = this.scene.tweens.timeline({
            tweens: tweens,
            onComplete: () =>
            {
                this.menuButton.setInteractive();
                this.nextButton.setInteractive();
                this.backButton.setInteractive();
            }
        });

        let waitForTimeline = new Promise((resolve) =>
        {
            timeline.on(Phaser.Tweens.Events.TIMELINE_COMPLETE, () => resolve());
        })
        await waitForTimeline;
    }

    /**
     * Animates the state out.
     * @param {string} nextState the GameState enum value to go to
     */
    async leave(nextState)
    {
        this.menuButton.disableInteractive();
        this.nextButton.disableInteractive();
        this.backButton.disableInteractive();

        ABCya.Audio.playOneShot(SoundAssetKeys._shapes_review_tween, this.scene);

        let tweens = [
            { targets: this.letterS, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterS.offScreenPosition.y }, offset: 500 },
            { targets: this.letterH, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterH.offScreenPosition.y }, offset: 450 },
            { targets: this.letterA, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterA.offScreenPosition.y }, offset: 400 },
            { targets: this.letterP, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterP.offScreenPosition.y }, offset: 350 },
            { targets: this.letterE1, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterE1.offScreenPosition.y }, offset: 300 },
            { targets: this.letterR, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterR.offScreenPosition.y }, offset: 250 },
            { targets: this.letterE2, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterE2.offScreenPosition.y }, offset: 200 },
            { targets: this.letterV, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterV.offScreenPosition.y }, offset: 150 },
            { targets: this.letterI, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterI.offScreenPosition.y }, offset: 100 },
            { targets: this.letterE3, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterE3.offScreenPosition.y }, offset: 50 },
            { targets: this.letterW, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterW.offScreenPosition.y }, offset: 0 },
            { targets: this.menuButton, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { scale: 0 }, offset: 200 },
            { targets: this.backButton, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { scale: 0 }, offset: 100 },
            { targets: this.nextButton, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { scale: 0 }, offset: 0 },
        ];

        if (nextState != GameStates.TITLE)
        {
            tweens.push({ targets: this.scene.titleScreen.jingleAudio, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { volume: 0 }, offset: 500, onComplete: () => this.scene.titleScreen.jingleAudio.stop() });
        }

        if (this._currentState == ShapeReviewStates.FLAT_SHAPES)
        {
            tweens.push(
                { targets: this.flatShapesTop, duration: 1000, ease: Phaser.Math.Easing.Quadratic.In, props: { x: this.flatShapesTop.offScreenPosition.x }, offset: 0 },
                { targets: this.flatShapesBottom, duration: 1000, ease: Phaser.Math.Easing.Quadratic.In, props: { x: this.flatShapesBottom.offScreenPosition.x }, offset: 0 },
                { targets: this.flatShapesFooter, duration: 1000, ease: Phaser.Math.Easing.Quadratic.In, props: { alpha: 0 }, offset: 0 },
            );
        }
        else if (this._currentState == ShapeReviewStates.PLANE_SHAPES)
        {
            tweens.push(
                { targets: this.planeShapesTop, duration: 1000, ease: Phaser.Math.Easing.Quadratic.In, props: { x: this.planeShapesTop.offScreenPosition.x }, offset: 0 },
                { targets: this.planeShapesBottom, duration: 1000, ease: Phaser.Math.Easing.Quadratic.In, props: { x: this.planeShapesBottom.offScreenPosition.x }, offset: 0 },
                { targets: this.planeShapesFooter, duration: 1000, ease: Phaser.Math.Easing.Quadratic.In, props: { alpha: 0 }, offset: 0 },
            );
        }
        else if (this._currentState == ShapeReviewStates.THREE_D_SHAPES)
        {
            tweens.push(
                { targets: this.threeDShapesTop, duration: 1000, ease: Phaser.Math.Easing.Quadratic.In, props: { x: this.threeDShapesTop.offScreenPosition.x }, offset: 0 },
                { targets: this.threeDShapesBottom, duration: 1000, ease: Phaser.Math.Easing.Quadratic.In, props: { x: this.threeDShapesBottom.offScreenPosition.x }, offset: 0 },
                { targets: this.threeDShapesFooter, duration: 1000, ease: Phaser.Math.Easing.Quadratic.In, props: { alpha: 0 }, offset: 0 },
            );
        }

        let timeline = this.scene.tweens.timeline({
            tweens: tweens,
        });

        let waitForTimeline = new Promise((resolve) =>
        {
            timeline.on(Phaser.Tweens.Events.TIMELINE_COMPLETE, () => resolve());
        })
        await waitForTimeline;
    }

    /**
     * Add a reference image to this state.
     * @param {Boolean} enablePlacer whether to enable the image placer debug tool after adding the reference image
     */
    _addReference(key, path, enablePlacer = true)
    {
        ABCya.Debug.addScreenReference(this.scene, key, path, (image) =>
        {
            if (enablePlacer) ABCya.Debug.enableImagePlacerDebug(this.scene, this.scene.background, image);
        });
    }

    /**
     * Add the header graphics.
     */
    _addHeader()
    {
        this.letterS = ABCya.Images.addImage(244, 82, ImageKeys._02_Shape_Review_Title_Title_S, this);
        this._setAnimationPositions(this.letterS, { x: this.letterS.x, y: -190 });

        this.letterH = ABCya.Images.addImage(306, 84, ImageKeys._02_Shape_Review_Title_Title_H, this);
        this._setAnimationPositions(this.letterH, { x: this.letterH.x, y: -190 });

        this.letterA = ABCya.Images.addImage(369, 84, ImageKeys._02_Shape_Review_Title_Title_A, this);
        this._setAnimationPositions(this.letterA, { x: this.letterA.x, y: -190 });

        this.letterP = ABCya.Images.addImage(432, 74, ImageKeys._02_Shape_Review_Title_Title_P, this);
        this._setAnimationPositions(this.letterP, { x: this.letterP.x, y: -190 });

        this.letterE1 = ABCya.Images.addImage(493, 85, ImageKeys._02_Shape_Review_Title_Title_E, this);
        this._setAnimationPositions(this.letterE1, { x: this.letterE1.x, y: -190 });

        this.letterR = ABCya.Images.addImage(594, 84, ImageKeys._02_Shape_Review_Title_Title_R, this);
        this._setAnimationPositions(this.letterR, { x: this.letterR.x, y: -190 });

        this.letterE2 = ABCya.Images.addImage(656, 85, ImageKeys._02_Shape_Review_Title_Title_E, this);
        this._setAnimationPositions(this.letterE2, { x: this.letterE2.x, y: -190 });

        this.letterV = ABCya.Images.addImage(721, 81, ImageKeys._02_Shape_Review_Title_Title_V, this);
        this._setAnimationPositions(this.letterV, { x: this.letterV.x, y: -190 });

        this.letterI = ABCya.Images.addImage(771, 84, ImageKeys._02_Shape_Review_Title_Title_I, this);
        this._setAnimationPositions(this.letterI, { x: this.letterI.x, y: -190 });

        this.letterE3 = ABCya.Images.addImage(820, 86, ImageKeys._02_Shape_Review_Title_Title_E, this);
        this._setAnimationPositions(this.letterE3, { x: this.letterE3.x, y: -190 });

        this.letterW = ABCya.Images.addImage(906, 90, ImageKeys._02_Shape_Review_Title_Title_W, this);
        this._setAnimationPositions(this.letterW, { x: this.letterW.x, y: -190 });
    }

    /**
     * Set the on/off screen positions. These are the start/end positions when the game object is animating in.
     * @param {Phaser.GameObjects.GameObject} target the game object to add the animation positions to
     * @param {{x:number, y:number}} offScreenPosition the offscreen position (the position the game object starts at when animating in)
     */
    _setAnimationPositions(target, offScreenPosition)
    {
        target.onScreenPosition = { x: target.x, y: target.y };
        target.offScreenPosition = offScreenPosition;
    }

    /**
     * Add the buttons (menu/back/next) and needed listeners.
     */
    _addButtons()
    {
        this.menuButton = new CubeButton(this.scene,
            ImageKeys._Buttons_Shape_Review_Btn_Menu3,
            ImageKeys._Buttons_Shape_Review_Btn_Menu2,
            ImageKeys._Buttons_Shape_Review_Btn_Menu1).
            setPosition(81, 86);
        this._setAnimationPositions(this.menuButton, { x: this.menuButton.x, y: -190 });
        this.menuButton.on(CubeButtonEvents.CLICKED, () => this.scene.events.emit(GameEvents.SHOW_MENU));

        this.backButton = new CubeButton(this.scene,
            ImageKeys._Buttons_Shape_Review_Btn_Menu3,
            ImageKeys._Buttons_Shape_Review_Btn_Back2,
            ImageKeys._Buttons_Shape_Review_Bgtn_Back1).
            setPosition(81, 686);
        this._setAnimationPositions(this.backButton, { x: this.backButton.x, y: this.backButton.y + 200 });
        this.backButton.on(CubeButtonEvents.CLICKED, this._back, this);

        this.nextButton = new CubeButton(this.scene,
            ImageKeys._Buttons_Shape_Review_Btn_Menu3,
            ImageKeys._Buttons_Shape_Review_Btn_Next2,
            ImageKeys._Buttons_Shape_Review_Btn_Next1).
            setPosition(943, 686);
        this._setAnimationPositions(this.nextButton, { x: this.nextButton.x, y: this.nextButton.y + 200 });
        this.nextButton.on(CubeButtonEvents.CLICKED, this._next, this);

        this.add([this.menuButton, this.nextButton, this.backButton]);
    }

    /**
     * Go to the previous inner state. If at the first inner state (ShapeReviewStates.FLAT_SHAPES), this will
     * return to the Title state.
     */
    _back()
    {
        switch (this._currentState)
        {
            case ShapeReviewStates.FLAT_SHAPES:
                // go back to the title screen
                this.scene.events.emit(GameEvents.GO_TO_STATE, GameStates.TITLE);
                break;
            case ShapeReviewStates.PLANE_SHAPES:
                this.goToState(ShapeReviewStates.FLAT_SHAPES);
                break;
            case ShapeReviewStates.THREE_D_SHAPES:
                this.goToState(ShapeReviewStates.PLANE_SHAPES);
                break;
        }
    }

    /**
     * Go to the next inner state. If at the last inner state (ShapeReviewStates.THREE_D_SHAPES), this will
     * go to the beach state.
     */
    _next()
    {
        switch (this._currentState)
        {
            case ShapeReviewStates.FLAT_SHAPES:
                this.goToState(ShapeReviewStates.PLANE_SHAPES);
                break;
            case ShapeReviewStates.PLANE_SHAPES:
                this.goToState(ShapeReviewStates.THREE_D_SHAPES);
                break;
            case ShapeReviewStates.THREE_D_SHAPES:
                this.scene.events.emit(GameEvents.GO_TO_STATE, REGIONS.BEACH);
                break;
        }
    }

    /**
     * Add the shapes and their containers.
     */
    _addShapes()
    {
        this.flatShapesTop = this.scene.add.container();
        this.flatShapesTop.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Flat_Crcl, SHAPES.CIRCLE, SoundAssetKeys._circle).setPosition(140, 247));
        this.flatShapesTop.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Flat_Oval, SHAPES.OVAL, SoundAssetKeys._oval).setPosition(316, 247));
        this.flatShapesTop.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Flat_Sqr, SHAPES.SQUARE, SoundAssetKeys._square).setPosition(498, 247));
        this.flatShapesTop.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Flat_Rctngl, SHAPES.RECTANGLE, SoundAssetKeys._rectangle).setPosition(682, 247));
        this.flatShapesTop.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Flat_Trngl, SHAPES.TRIANGLE, SoundAssetKeys._triangle).setPosition(865, 247));
        this._setAnimationPositions(this.flatShapesTop, { x: -this.scene.scale.width, y: this.flatShapesTop.y });

        this.flatShapesBottom = this.scene.add.container();
        this.flatShapesBottom.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Flat_Octa, SHAPES.OCTAGON, SoundAssetKeys._octagon).setPosition(147, 479));
        this.flatShapesBottom.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Flat_Hexa, SHAPES.HEXAGON, SoundAssetKeys._hexagon).setPosition(320, 479));
        this.flatShapesBottom.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Flat_Penta, SHAPES.PENTAGON, SoundAssetKeys._pentagon).setPosition(505, 479));
        this.flatShapesBottom.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Flat_Crsnt, SHAPES.CRESCENT, SoundAssetKeys._crescent).setPosition(688, 479));
        this.flatShapesBottom.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Flat_Hrt, SHAPES.HEART, SoundAssetKeys._heart).setPosition(871, 479));
        this._setAnimationPositions(this.flatShapesBottom, { x: this.scene.scale.width, y: this.flatShapesBottom.y });

        this.planeShapesTop = this.scene.add.container();
        this.planeShapesTop.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Plane_Crcl, SHAPES.CIRCLE, SoundAssetKeys._circle, 115).setPosition(140, 247));
        this.planeShapesTop.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Plane_Oval, SHAPES.OVAL, SoundAssetKeys._oval, 115).setPosition(316, 247));
        this.planeShapesTop.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Plane_Sqr, SHAPES.SQUARE, SoundAssetKeys._square, 115).setPosition(498, 247));
        this.planeShapesTop.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Plane_Rctngl, SHAPES.RECTANGLE, SoundAssetKeys._rectangle, 115).setPosition(682, 247));
        this.planeShapesTop.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Plane_Trngl, SHAPES.TRIANGLE, SoundAssetKeys._triangle, 115).setPosition(865, 247));
        this._setAnimationPositions(this.planeShapesTop, { x: -this.scene.scale.width, y: this.planeShapesTop.y });

        this.planeShapesBottom = this.scene.add.container();
        this.planeShapesBottom.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Plane_Octa, SHAPES.OCTAGON, SoundAssetKeys._octagon).setPosition(147, 479));
        this.planeShapesBottom.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Plane_Hex, SHAPES.HEXAGON, SoundAssetKeys._hexagon).setPosition(320, 479));
        this.planeShapesBottom.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Plane_Penta, SHAPES.PENTAGON, SoundAssetKeys._pentagon).setPosition(505, 479));
        this.planeShapesBottom.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Plane_Crsnt, SHAPES.CRESCENT, SoundAssetKeys._crescent).setPosition(688, 479));
        this.planeShapesBottom.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_Plane_Hrt, SHAPES.HEART, SoundAssetKeys._heart).setPosition(871, 479));
        this._setAnimationPositions(this.planeShapesBottom, { x: this.scene.scale.width, y: this.planeShapesBottom.y });

        this.threeDShapesTop = this.scene.add.container();
        this.threeDShapesTop.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_3D_Sphere, SHAPES.SPHERE, SoundAssetKeys._sphere, 104).setPosition(170, 254));
        this.threeDShapesTop.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_3D_Cube, SHAPES.CUBE, SoundAssetKeys._cube, 104).setPosition(392, 254));
        this.threeDShapesTop.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_3D_Cone, SHAPES.CONE, SoundAssetKeys._cone, 104).setPosition(609, 254));
        this.threeDShapesTop.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_3D_Prmd, SHAPES.PYRAMID, SoundAssetKeys._pyramid, 104).setPosition(841, 254));
        this._setAnimationPositions(this.threeDShapesTop, { x: -this.scene.scale.width, y: this.threeDShapesTop.y });

        this.threeDShapesBottom = this.scene.add.container();
        this.threeDShapesBottom.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_3D_Clndr, SHAPES.CYLINDER, SoundAssetKeys._cylinder, 108).setPosition(219, 480));
        this.threeDShapesBottom.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_3D_RctPrsm, SHAPES.RECTANGULAR_PRISM, SoundAssetKeys._rectangluar_prism, 108).setPosition(485, 480));
        this.threeDShapesBottom.add(new Shape(this.scene, ImageKeys._02_Shape_Review_Shapes_3D_TrnglPrsm, SHAPES.TRIANGULAR_PRISM, SoundAssetKeys._triangular_prism, 108).setPosition(786, 480));
        this._setAnimationPositions(this.threeDShapesBottom, { x: this.scene.scale.width, y: this.threeDShapesBottom.y });

        this.add([this.flatShapesTop, this.flatShapesBottom, this.planeShapesTop, this.planeShapesBottom, this.threeDShapesTop, this.threeDShapesBottom]);
    }

    /**
     * Add the footer graphics (the text).
     */
    _addFooter()
    {
        this.flatShapesFooter = ABCya.Text.addText(512, 688, 58, 0x2a5e5e, FontKeys._retrigaregular, GameData.text.flatShapes, this).setOrigin(0.5);
        this.planeShapesFooter = ABCya.Text.addText(512, 688, 58, 0x2a5e5e, FontKeys._retrigaregular, GameData.text.flatShapesOn3dPlane, this).setOrigin(0.5);
        this.threeDShapesFooter = ABCya.Text.addText(512, 688, 58, 0x2a5e5e, FontKeys._retrigaregular, GameData.text.threeDimensionalShapes, this).setOrigin(0.5);
    }

    /**
     * Send all game objects to their start positions. Some game objects aren't sent "offscreen" but instead set to scale of 0 or alpha of 0.
     */
    _sendTargetsOffScreen()
    {
        this.letterS.setPosition(this.letterS.offScreenPosition.x, this.letterS.offScreenPosition.y);
        this.letterH.setPosition(this.letterH.offScreenPosition.x, this.letterH.offScreenPosition.y);
        this.letterA.setPosition(this.letterA.offScreenPosition.x, this.letterA.offScreenPosition.y);
        this.letterP.setPosition(this.letterP.offScreenPosition.x, this.letterP.offScreenPosition.y);
        this.letterE1.setPosition(this.letterE1.offScreenPosition.x, this.letterE1.offScreenPosition.y);
        this.letterR.setPosition(this.letterR.offScreenPosition.x, this.letterR.offScreenPosition.y);
        this.letterE2.setPosition(this.letterE2.offScreenPosition.x, this.letterE2.offScreenPosition.y);
        this.letterV.setPosition(this.letterV.offScreenPosition.x, this.letterV.offScreenPosition.y);
        this.letterI.setPosition(this.letterI.offScreenPosition.x, this.letterI.offScreenPosition.y);
        this.letterE3.setPosition(this.letterE3.offScreenPosition.x, this.letterE3.offScreenPosition.y);
        this.letterW.setPosition(this.letterW.offScreenPosition.x, this.letterW.offScreenPosition.y);

        this.flatShapesTop.setPosition(this.flatShapesTop.offScreenPosition.x, this.flatShapesTop.offScreenPosition.y);
        this.flatShapesBottom.setPosition(this.flatShapesBottom.offScreenPosition.x, this.flatShapesBottom.offScreenPosition.y);

        this.planeShapesTop.setPosition(this.planeShapesTop.offScreenPosition.x, this.planeShapesTop.offScreenPosition.y);
        this.planeShapesBottom.setPosition(this.planeShapesBottom.offScreenPosition.x, this.planeShapesBottom.offScreenPosition.y);

        this.threeDShapesTop.setPosition(this.threeDShapesTop.offScreenPosition.x, this.threeDShapesTop.offScreenPosition.y);
        this.threeDShapesBottom.setPosition(this.threeDShapesBottom.offScreenPosition.x, this.threeDShapesBottom.offScreenPosition.y);

        this.menuButton.setScale(0);
        this.backButton.setScale(0);
        this.nextButton.setScale(0);

        this.flatShapesFooter.setAlpha(0);
        this.planeShapesFooter.setAlpha(0);
        this.threeDShapesFooter.setAlpha(0);
    }

    /**
     * Sends the inner state to the passed in state. Does nothing if the current state is already the desired state.
     * This function will return once the new state is active.
     * @param {string} state the inner state to go to
     */
    async goToState(state)
    {
        if (this._currentState == state) return;

        ABCya.Audio.playOneShot(SoundAssetKeys._shapes_review_tween, this.scene, 1, false, 0.2);

        await this._leaveCurrentState();

        this._currentState = state;
        ABCya.Audio.playOneShot(SoundAssetKeys._shapes_review_tween, this.scene, 1, false, 0.2);

        await this._enterCurrentState();
    }

    /**
     * Calls the animate in for the current state and waits for the animation to complete.
     */
    async _enterCurrentState()
    {
        switch (this._currentState)
        {
            case ShapeReviewStates.FLAT_SHAPES:
                await this._animateStateIn(this.flatShapesTop, this.flatShapesBottom, this.flatShapesFooter);
                break;
            case ShapeReviewStates.PLANE_SHAPES:
                await this._animateStateIn(this.planeShapesTop, this.planeShapesBottom, this.planeShapesFooter);
                break;
            case ShapeReviewStates.THREE_D_SHAPES:
                await this._animateStateIn(this.threeDShapesTop, this.threeDShapesBottom, this.threeDShapesFooter);
                break;
        }
    }

    /**
     * Calls the animate out for the current state and waits for the animation to complete.
     */
    async _leaveCurrentState()
    {
        switch (this._currentState)
        {
            case ShapeReviewStates.FLAT_SHAPES:
                await this._animateStateOut(this.flatShapesTop, this.flatShapesBottom, this.flatShapesFooter);
                break;
            case ShapeReviewStates.PLANE_SHAPES:
                await this._animateStateOut(this.planeShapesTop, this.planeShapesBottom, this.planeShapesFooter);
                break;
            case ShapeReviewStates.THREE_D_SHAPES:
                await this._animateStateOut(this.threeDShapesTop, this.threeDShapesBottom, this.threeDShapesFooter);
                break;
        }
    }

    /**
     * Animates the state out. This causes the top and bottom row of shapes to slide off and the text to fade off.
     * @param {Phaser.GameObjects.Container} shapesTopRow the top row of shapes to animate off
     * @param {Phaser.GameObjects.Container} shapesBottomRow the bottom row of shapes to animate off
     * @param {Phaser.GameObjects.Text} shapesFooter the bottom footer text to animate off
     */
    async _animateStateOut(shapesTopRow, shapesBottomRow, shapesFooter)
    {
        this.menuButton.disableInteractive();
        this.nextButton.disableInteractive();
        this.backButton.disableInteractive();

        let tweens = [
            { targets: shapesTopRow, duration: 1000, ease: Phaser.Math.Easing.Quadratic.In, props: { x: shapesTopRow.offScreenPosition.x }, offset: 0 },
            { targets: shapesBottomRow, duration: 1000, ease: Phaser.Math.Easing.Quadratic.In, props: { x: shapesBottomRow.offScreenPosition.x }, offset: 0 },
            { targets: shapesFooter, duration: 1000, ease: Phaser.Math.Easing.Quadratic.In, props: { alpha: 0 }, offset: 0 },
        ];

        let timeline = this.scene.tweens.timeline({
            tweens: tweens,
        });

        let waitForTimeline = new Promise((resolve) =>
        {
            timeline.on(Phaser.Tweens.Events.TIMELINE_COMPLETE, () => resolve());
        })
        await waitForTimeline;
    }

    /**
     * Animates the state in. This causes the top and bottom row of shapes to slide on and the text to fade in.
     * @param {Phaser.GameObjects.Container} shapesTopRow the top row of shapes to animate on
     * @param {Phaser.GameObjects.Container} shapesBottomRow the bottom row of shapes to animate on
     * @param {Phaser.GameObjects.Text} shapesFooter the bottom footer text to animate on
     */
    async _animateStateIn(shapesTopRow, shapesBottomRow, shapesFooter)
    {
        this.menuButton.disableInteractive();
        this.nextButton.disableInteractive();
        this.backButton.disableInteractive();

        let tweens = [
            { targets: shapesTopRow, duration: 1000, ease: Phaser.Math.Easing.Quadratic.Out, props: { x: shapesTopRow.onScreenPosition.x }, offset: 0 },
            { targets: shapesBottomRow, duration: 1000, ease: Phaser.Math.Easing.Quadratic.Out, props: { x: shapesBottomRow.onScreenPosition.x }, offset: 0 },
            { targets: shapesFooter, duration: 1000, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: 0 },
        ];

        let timeline = this.scene.tweens.timeline({
            tweens: tweens,
            onComplete: () =>
            {
                this.menuButton.setInteractive();
                this.nextButton.setInteractive();
                this.backButton.setInteractive();
            }
        });

        let waitForTimeline = new Promise((resolve) =>
        {
            timeline.on(Phaser.Tweens.Events.TIMELINE_COMPLETE, () => resolve());
        })
        await waitForTimeline;
    }
}

/**
 * The inner states for this state.
 */
const ShapeReviewStates = {
    FLAT_SHAPES: "FlatShapes",
    PLANE_SHAPES: "PlaneShapes",
    THREE_D_SHAPES: "3DShapes"
}